import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, MousePointer, CheckCircle, Star, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AffiliateAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'Home Staging', label: 'Home Staging' },
    { value: 'Photography & Videography', label: 'Photography' },
    { value: 'Painting Services', label: 'Painting' },
    { value: 'Cleaning Services', label: 'Cleaning' },
    { value: 'Handyman Services', label: 'Handyman' },
    { value: 'Landscaping & Yard Work', label: 'Landscaping' },
    { value: 'Title Companies', label: 'Title Companies' },
    { value: 'Real Estate Attorneys', label: 'Attorneys' },
    { value: 'Home Inspectors', label: 'Home Inspectors' }
  ];

  useEffect(() => {
    fetchAnalytics();
    fetchRatings();
  }, [selectedCategory]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const response = await axios.get(`${API}/admin/affiliate-analytics`, {
        params,
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchRatings = async () => {
    try {
      const response = await axios.get(`${API}/admin/service-ratings`, {
        withCredentials: true
      });
      setRatings(response.data.ratings || []);
    } catch (error) {
      toast.error('Failed to load ratings');
    }
  };

  const toggleRatingDisplay = async (ratingId) => {
    try {
      await axios.post(`${API}/admin/service-ratings/${ratingId}/toggle-display`, {}, {
        withCredentials: true
      });
      toast.success('Rating visibility updated');
      fetchRatings();
    } catch (error) {
      toast.error('Failed to update rating');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Affiliate Analytics & Performance</h2>
          <p className="text-gray-600">Track referral clicks, conversions, and user ratings</p>
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      {analytics && (
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <MousePointer className="w-8 h-8 text-blue-600" />
            </div>
            <div className="text-3xl font-bold text-blue-900">{analytics.summary.total_clicks}</div>
            <div className="text-sm text-blue-700">Total Clicks</div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <div className="text-3xl font-bold text-green-900">{analytics.summary.total_conversions}</div>
            <div className="text-sm text-green-700">Conversions</div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-purple-600" />
            </div>
            <div className="text-3xl font-bold text-purple-900">{analytics.summary.conversion_rate}%</div>
            <div className="text-sm text-purple-700">Conversion Rate</div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <div className="flex items-center justify-between mb-2">
              <Star className="w-8 h-8 text-yellow-600" />
            </div>
            <div className="text-3xl font-bold text-yellow-900">{analytics.summary.total_ratings}</div>
            <div className="text-sm text-yellow-700">Total Ratings</div>
          </Card>
        </div>
      )}

      {/* Provider Performance Table */}
      {analytics && analytics.by_provider && analytics.by_provider.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Performance by Provider</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Clicks</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversions</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conv. Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ratings</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {analytics.by_provider.map((provider, idx) => {
                  const convRate = provider.total_clicks > 0 
                    ? ((provider.conversions / provider.total_clicks) * 100).toFixed(1) 
                    : 0;
                  return (
                    <tr key={idx}>
                      <td className="px-4 py-3 font-medium text-gray-900">{provider._id}</td>
                      <td className="px-4 py-3 text-gray-600">{provider.total_clicks}</td>
                      <td className="px-4 py-3 text-gray-600">{provider.conversions}</td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${
                          convRate >= 20 ? 'text-green-600' :
                          convRate >= 10 ? 'text-yellow-600' :
                          'text-gray-600'
                        }`}>
                          {convRate}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{provider.ratings_submitted}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Recent Ratings */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Service Ratings ({ratings.length})</h3>
        {ratings.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No ratings submitted yet</p>
        ) : (
          <div className="space-y-3">
            {ratings.slice(0, 20).map((rating) => (
              <div
                key={rating.id}
                className={`border rounded-lg p-4 ${
                  rating.displayed ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-300 opacity-60'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900">{rating.provider_name}</span>
                      <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{rating.provider_category}</span>
                      {!rating.displayed && (
                        <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded font-semibold">
                          Hidden
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= rating.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-gray-600">
                        by {rating.user_name || 'Anonymous'} • {new Date(rating.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {rating.review && (
                      <p className="text-sm text-gray-700 italic">"{rating.review}"</p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleRatingDisplay(rating.id)}
                    className={rating.displayed ? 'text-red-600 border-red-300' : 'text-green-600 border-green-300'}
                  >
                    {rating.displayed ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-1" />
                        Hide
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1" />
                        Show
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AffiliateAnalytics;
